// Copy and modify from https://gitlab.agodadev.io/full-stack/monoliths/agoda-com-spa-mobile repo
// Path: Agoda.Mobile.Client/src/module/accommodation/client/search/src/searchResult/sections/resultList/hlc/propertyCardTopSection/components/topBadges/LoyaltyBadge.tsx

import React from 'react';
import { Box, type BoxProps, Icon, Typography } from '@drone-js/core';
import { BackgroundType, TextType } from '@drone-js/theme';

export enum LoyaltyTier {
    Diamond = 'Diamond',
    Platinum = 'Platinum',
    Gold = 'Gold',
    Silver = 'Silver',
    Bronze = 'Bronze',
}

interface OwnProps extends BoxProps {
    tier: LoyaltyTier;
    label: string;
}

type LoyaltyBadgeProps = OwnProps;

interface VipColor {
    background: BackgroundType;
    backgroundGradient: BackgroundType;
    text: TextType;
}

const getVipColor = (loyaltyTier: LoyaltyTier): VipColor => {
    switch (loyaltyTier) {
        case LoyaltyTier.Bronze:
            return {
                background: 'product.vip-bronze',
                backgroundGradient: 'product.vip-bronze.gradient',
                text: 'product.on.vip-bronze',
            };
        case LoyaltyTier.Silver:
            return {
                background: 'product.vip-silver',
                backgroundGradient: 'product.vip-silver.gradient',
                text: 'product.on.vip-silver',
            };
        case LoyaltyTier.Gold:
            return {
                background: 'product.vip-gold',
                backgroundGradient: 'product.vip-gold.gradient',
                text: 'product.on.vip-gold',
            };
        case LoyaltyTier.Platinum:
            return {
                background: 'product.vip-platinum',
                backgroundGradient: 'product.vip-platinum.gradient',
                text: 'product.on.vip-platinum',
            };
        case LoyaltyTier.Diamond:
            return {
                background: 'product.vip-prime',
                backgroundGradient: 'product.vip-prime.gradient',
                text: 'product.on.vip-prime',
            };
        default:
            return {
                background: 'product.vip-bronze',
                backgroundGradient: 'product.vip-bronze.gradient',
                text: 'product.on.vip-bronze',
            };
    }
};

const LoyaltyBadge: React.FC<LoyaltyBadgeProps> = ({ tier, label, ...boxProps }) => {
    const vipColor = getVipColor(tier);

    return (
        <Box
            position="relative"
            display="inline-flex"
            borderRadius="2"
            overflow="hidden"
            minHeight="20px"
            maxWidth="fit-content"
            backgroundColor="base.surface.inverted"
            dir="ltr"
            {...boxProps}
        >
            <Box display="flex" alignItems="center" justifyContent="center" gap="2" minWidth="28.5px" zIndex={2}>
                <Icon id="fill.brand.vipstar" size="12" color="base.inverted" />
            </Box>
            <Box
                position="relative"
                display="flex"
                flex="none"
                alignItems="center"
                zIndex={1}
                px="8"
                backgroundColor={vipColor.backgroundGradient}
                borderRadius={['0', '2', '2', '0']}
                data-testid="background-gradient"
            >
                <Box display="inline-flex" data-testid="text-wrapper">
                    <Box width="4px" height="1px" />
                    <Typography variant="caption-default" textColor={vipColor.text} display="inline-flex" data-testid="text">
                        {label}
                    </Typography>
                </Box>
                <Box
                    width="4px"
                    height="60px"
                    position="absolute"
                    bottom="-12px"
                    left="3px"
                    backgroundColor={vipColor.background}
                    transform="rotate(20deg)"
                    zIndex={1}
                    data-testid="line"
                />
                <Box width="8px" height="60px" position="absolute" bottom="-12px" left="-4px" backgroundColor="base.surface.inverted" transform="rotate(20deg)" zIndex={-1} />
            </Box>
        </Box>
    );
};

LoyaltyBadge.displayName = 'LoyaltyBadge';

export default LoyaltyBadge;
